// src/pages/AdminDashboard.js — Old-style clean UI + Round 2 features
import React, { useState, useEffect, useRef, useMemo } from "react";
import { db } from "../firebase";
import { useAuth } from "../AuthContext";
import { useToast } from "../components/Toast";
import Navbar from "../components/Navbar";
import StatusBadge from "../components/StatusBadge";
import ChatBubble from "../components/ChatBubble";
import { SkeletonList } from "../components/Skeleton";
import {
  SERVICES, ADMIN_UID, ORDER_STATUSES, sendStatusEmail, playNotificationSound,
  copyToClipboard, getUPSTrackingUrl, exportOrdersCSV, estimateShipping, formatFullDate,
} from "../config";
import {
  collection, query, orderBy, onSnapshot, doc, updateDoc, addDoc, Timestamp, limit as firestoreLimit, getDoc, setDoc,
} from "firebase/firestore";

export default function AdminDashboard() {
  const { user } = useAuth();
  const toast = useToast();
  const fileRefs = useRef({});
  const prevCount = useRef(0);

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [quoteInputs, setQuoteInputs] = useState({});
  const [trackingInputs, setTrackingInputs] = useState({});
  const [uploading, setUploading] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [costInputs, setCostInputs] = useState({});
  const [editingQuote, setEditingQuote] = useState(null);
  const [expandedTimeline, setExpandedTimeline] = useState(new Set());
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [bulkQuotePrice, setBulkQuotePrice] = useState("");
  const [showActivityLog, setShowActivityLog] = useState(false);
  const [activityLog, setActivityLog] = useState([]);
  const [showCreditModal, setShowCreditModal] = useState(null); // holds {uid, name, email}
  const [creditAmount, setCreditAmount] = useState("");
  const [creditNote, setCreditNote] = useState("");
  const [creditType, setCreditType] = useState("add"); // "add" or "deduct"
  const [userBalances, setUserBalances] = useState({}); // uid -> balance

  const isAdmin = user?.uid === ADMIN_UID;

  useEffect(() => {
    if (!isAdmin) return;
    const q = query(collection(db, "orders"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      if (prevCount.current > 0 && list.length > prevCount.current) {
        playNotificationSound("success");
        toast.success("New order received!");
      }
      prevCount.current = list.length;
      setOrders(list);
      setLoading(false);
    });
    return unsub;
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    const q = query(
      collection(db, "admin_activity_log"),
      orderBy("timestamp", "desc"),
      firestoreLimit(50)
    );
    const unsub = onSnapshot(q, (snap) => {
      setActivityLog(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [isAdmin]);

  // Load user balances from unique users in orders
  useEffect(() => {
    if (!isAdmin || orders.length === 0) return;
    const uniqueUids = [...new Set(orders.map(o => o.userId).filter(Boolean))];
    uniqueUids.forEach(async (uid) => {
      try {
        const snap = await getDoc(doc(db, "users", uid));
        if (snap.exists()) {
          setUserBalances(prev => ({ ...prev, [uid]: parseFloat(snap.data().balance) || 0 }));
        }
      } catch (e) { console.error("Failed to load balance for", uid, e); }
    });
  }, [isAdmin, orders]);

  const handleCreditUser = async () => {
    if (!showCreditModal) return;
    const amount = parseFloat(creditAmount);
    if (!amount || isNaN(amount) || amount <= 0) return toast.error("Enter a valid amount");
    const { uid, name, email } = showCreditModal;
    const currentBalance = userBalances[uid] || 0;
    const newBalance = creditType === "add" ? currentBalance + amount : Math.max(0, currentBalance - amount);
    try {
      // Update user balance
      await setDoc(doc(db, "users", uid), { balance: newBalance }, { merge: true });
      // Log the transaction
      await addDoc(collection(db, "balance_history"), {
        userId: uid, userName: name || "", userEmail: email || "",
        type: creditType, amount, previousBalance: currentBalance, newBalance,
        note: creditNote || (creditType === "add" ? "Zelle payment received" : "Balance deduction"),
        adminEmail: user?.email || "admin", createdAt: Timestamp.now(),
      });
      await logActivity(creditType === "add" ? "Added credit" : "Deducted credit", null,
        `$${amount.toFixed(2)} ${creditType === "add" ? "to" : "from"} ${name || email} (new balance: $${newBalance.toFixed(2)})`);
      setUserBalances(prev => ({ ...prev, [uid]: newBalance }));
      toast.success(`${creditType === "add" ? "Added" : "Deducted"} $${amount.toFixed(2)} — new balance: $${newBalance.toFixed(2)}`);
      setShowCreditModal(null); setCreditAmount(""); setCreditNote(""); setCreditType("add");
    } catch (e) { console.error(e); toast.error("Failed to update balance"); }
  };

  // Security check
  if (!isAdmin) {
    return (
      <div className="page-bg">
        <Navbar />
        <div className="container">
          <div className="card" style={{ marginTop: "2rem", textAlign: "center" }}>
            <h2 style={{ color: "#d4a053" }}>Access Denied</h2>
            <p>You do not have permission to access this page.</p>
          </div>
        </div>
      </div>
    );
  }

  const logActivity = async (action, orderId, details = "") => {
    try {
      await addDoc(collection(db, "admin_activity_log"), {
        action,
        orderId: orderId || null,
        details,
        adminEmail: user?.email || "admin",
        timestamp: Timestamp.now(),
      });
    } catch (err) {
      console.error("Failed to log activity:", err);
    }
  };

  // ── Actions ──

  const sendQuote = async (orderId) => {
    const price = parseFloat(quoteInputs[orderId]);
    if (!price || isNaN(price) || price < 0) return toast.error("Enter a valid price");
    try {
      await updateDoc(doc(db, "orders", orderId), {
        status: "quoted", price, quotedPrice: price.toFixed(2), quotedAt: Timestamp.now(),
      });
      await logActivity("Sent quote", orderId, "$" + price.toFixed(2));
      const order = orders.find((o) => o.id === orderId);
      if (order) {
        await sendStatusEmail(order.userEmail, order.userName, orderId, "quote_sent", `Quote: $${price.toFixed(2)}`);
      }
      setQuoteInputs((p) => ({ ...p, [orderId]: "" }));
      toast.success("Quote sent!");
    } catch (err) {
      toast.error("Failed to send quote");
    }
  };

  const uploadLabel = async (orderId, file) => {
    if (!file) return;
    if (file.size > 800 * 1024) return toast.error("File must be under 800 KB");
    setUploading((p) => ({ ...p, [orderId]: true }));
    try {
      // Read file as base64 and store in Firestore (no Firebase Storage needed)
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      // Save label but DON'T mark as completed — admin still needs to add tracking and hit Complete
      await updateDoc(doc(db, "orders", orderId), {
        labelData: base64, labelFileName: file.name, labelUploadedAt: Timestamp.now(),
      });
      await logActivity("Uploaded label", orderId, file.name);
      toast.success("Label uploaded! Now add tracking and hit Complete.");
    } catch (err) {
      toast.error("Upload failed: " + err.message);
    }
    setUploading((p) => ({ ...p, [orderId]: false }));
    if (fileRefs.current[orderId]) fileRefs.current[orderId].value = "";
  };

  const handleVoid = async (orderId, approved) => {
    try {
      const newStatus = approved ? "voided" : "completed";
      await updateDoc(doc(db, "orders", orderId), {
        status: newStatus, voidResolvedAt: Timestamp.now(),
      });
      await logActivity(approved ? "Approved void" : "Denied void", orderId);
      const order = orders.find((o) => o.id === orderId);
      if (order) {
        const emailType = approved ? "void_approved" : "void_denied";
        await sendStatusEmail(order.userEmail, order.userName, orderId, emailType, "");
      }
      toast.success(approved ? "Void approved" : "Void denied — order restored");
    } catch (err) {
      toast.error("Failed to process void");
    }
  };

  const adminVoidOrder = async (orderId) => {
    try {
      const order = orders.find((o) => o.id === orderId);
      if (!order) return toast.error("Order not found");
      await updateDoc(doc(db, "orders", orderId), {
        status: "voided",
        labelData: null,
        labelFileName: null,
        labelUrl: null,
        voidResolvedAt: Timestamp.now(),
      });
      await logActivity("Admin voided order", orderId);
      await sendStatusEmail(order.userEmail, order.userName, orderId, "void_approved", "Order voided by admin");
      toast.success("Order voided!");
    } catch (err) {
      toast.error("Failed to void order: " + err.message);
    }
  };

  const saveQuoteEdit = async (orderId, newPrice) => {
    const price = parseFloat(newPrice);
    if (!price || isNaN(price) || price < 0) return toast.error("Enter a valid price");
    try {
      await updateDoc(doc(db, "orders", orderId), {
        price, quotedPrice: price.toFixed(2),
      });
      await logActivity("Updated quote", orderId, "$" + price.toFixed(2));
      setEditingQuote(null);
      toast.success("Quote updated!");
    } catch (err) {
      toast.error("Failed to update quote: " + err.message);
    }
  };

  const toggleTimeline = (orderId) => {
    const newSet = new Set(expandedTimeline);
    if (newSet.has(orderId)) {
      newSet.delete(orderId);
    } else {
      newSet.add(orderId);
    }
    setExpandedTimeline(newSet);
  };

  const toggleSelectOrder = (orderId) => {
    const newSet = new Set(selectedOrders);
    if (newSet.has(orderId)) {
      newSet.delete(orderId);
    } else {
      newSet.add(orderId);
    }
    setSelectedOrders(newSet);
  };

  const bulkQuoteOrders = async () => {
    const price = parseFloat(bulkQuotePrice);
    if (!price || isNaN(price) || price < 0) return toast.error("Enter a valid price");
    try {
      const pendingSelected = Array.from(selectedOrders).filter((id) => {
        const order = orders.find((o) => o.id === id);
        return order && order.status === "pending";
      });
      for (const orderId of pendingSelected) {
        await updateDoc(doc(db, "orders", orderId), {
          status: "quoted", price, quotedPrice: price.toFixed(2), quotedAt: Timestamp.now(),
        });
        const order = orders.find((o) => o.id === orderId);
        if (order) {
          await sendStatusEmail(order.userEmail, order.userName, orderId, "quote_sent", `Quote: $${price.toFixed(2)}`);
        }
      }
      await logActivity("Bulk quoted", null, pendingSelected.length + " orders at $" + price.toFixed(2));
      setSelectedOrders(new Set());
      setBulkQuotePrice("");
      toast.success(`Quoted ${pendingSelected.length} orders!`);
    } catch (err) {
      toast.error("Bulk quote failed: " + err.message);
    }
  };

  const bulkVoidOrders = async () => {
    try {
      const completedSelected = Array.from(selectedOrders).filter((id) => {
        const order = orders.find((o) => o.id === id);
        return order && order.status === "completed";
      });
      for (const orderId of completedSelected) {
        const order = orders.find((o) => o.id === orderId);
        if (order) {
          await updateDoc(doc(db, "orders", orderId), {
            status: "voided", labelData: null, labelFileName: null, labelUrl: null, voidResolvedAt: Timestamp.now(),
          });
          await sendStatusEmail(order.userEmail, order.userName, orderId, "void_approved", "Order voided by admin");
        }
      }
      await logActivity("Bulk voided", null, completedSelected.length + " orders");
      setSelectedOrders(new Set());
      toast.success(`Voided ${completedSelected.length} orders!`);
    } catch (err) {
      toast.error("Bulk void failed: " + err.message);
    }
  };

  const completeWithTracking = async (orderId) => {
    const tracking = trackingInputs[orderId]?.trim();
    if (!tracking) return toast.error("Enter a tracking number");
    try {
      const order = orders.find((o) => o.id === orderId);
      await updateDoc(doc(db, "orders", orderId), {
        status: "completed", trackingNumber: tracking,
        completedAt: Timestamp.now(),
      });
      // Auto-deduct balance from customer when order is completed
      if (order && order.userId && order.quotedPrice) {
        const price = parseFloat(order.quotedPrice);
        if (price > 0) {
          try {
            const userSnap = await getDoc(doc(db, "users", order.userId));
            const currentBalance = userSnap.exists() ? (parseFloat(userSnap.data().balance) || 0) : 0;
            const newBalance = Math.max(0, currentBalance - price);
            await setDoc(doc(db, "users", order.userId), { balance: newBalance }, { merge: true });
            await addDoc(collection(db, "balance_history"), {
              userId: order.userId, userName: order.userName || "", userEmail: order.userEmail || "",
              type: "deduct", amount: price, previousBalance: currentBalance, newBalance,
              note: `Order #${orderId.slice(0, 8)} completed`, adminEmail: user?.email || "admin", createdAt: Timestamp.now(),
            });
            setUserBalances(prev => ({ ...prev, [order.userId]: newBalance }));
            await logActivity("Auto-deducted balance", orderId, `$${price.toFixed(2)} from ${order.userName || order.userEmail}`);
          } catch (balErr) { console.error("Balance deduction failed:", balErr); }
        }
      }
      await logActivity("Completed order", orderId, "Tracking: " + tracking);
      if (order) {
        const hasLabel = order.labelData || order.labelFileName;
        await sendStatusEmail(order.userEmail, order.userName, orderId, "label_ready", `Tracking: ${tracking}${hasLabel ? " — Label attached" : ""}`);
      }
      setTrackingInputs((p) => ({ ...p, [orderId]: "" }));
      toast.success("Order completed with tracking! Balance deducted.");
    } catch (err) {
      toast.error("Failed to complete: " + err.message);
    }
  };

  const saveCost = async (orderId) => {
    const cost = parseFloat(costInputs[orderId]);
    if (!cost || isNaN(cost) || cost < 0) return toast.error("Enter a valid cost");
    try {
      await updateDoc(doc(db, "orders", orderId), { adminCost: cost });
      await logActivity("Saved cost", orderId, "$" + cost.toFixed(2));
      setCostInputs((p) => ({ ...p, [orderId]: "" }));
      toast.success("Cost saved!");
    } catch (err) {
      toast.error("Failed to save cost: " + err.message);
    }
  };

  // ── Revenue calculations (only completed orders, NOT voided) ──
  const revenue = useMemo(() => {
    const completed = orders.filter((o) => o.status === "completed");
    const totalRevenue = completed.reduce((sum, o) => sum + (parseFloat(o.quotedPrice) || o.price || 0), 0);
    const totalCost = completed.reduce((sum, o) => sum + (o.adminCost || 0), 0);
    const totalProfit = totalRevenue - totalCost;
    const orderCount = completed.length;
    return { totalRevenue, totalCost, totalProfit, orderCount };
  }, [orders]);

  // ── Analytics calculations ──
  const analytics = useMemo(() => {
    const completed = orders.filter((o) => o.status === "completed");
    const last6Months = {};
    for (let i = 0; i < 6; i++) {
      const d = new Date();
      d.setMonth(d.getMonth() - i);
      const key = d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
      last6Months[key] = 0;
    }
    completed.forEach((o) => {
      if (o.completedAt) {
        const date = o.completedAt.toDate ? o.completedAt.toDate() : new Date(o.completedAt);
        const key = date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
        if (key in last6Months) {
          last6Months[key] += parseFloat(o.quotedPrice) || o.price || 0;
        }
      }
    });

    const avgOrderValue = completed.length > 0 ? completed.reduce((sum, o) => sum + (parseFloat(o.quotedPrice) || o.price || 0), 0) / completed.length : 0;

    const customerRevenue = {};
    completed.forEach((o) => {
      const customer = o.userName || "Unknown";
      customerRevenue[customer] = (customerRevenue[customer] || 0) + (parseFloat(o.quotedPrice) || o.price || 0);
    });
    const topCustomers = Object.entries(customerRevenue).sort((a, b) => b[1] - a[1]).slice(0, 5);

    const serviceBreakdown = {};
    orders.forEach((o) => {
      if (o.status === "completed") {
        const service = o.service || "Unknown";
        serviceBreakdown[service] = (serviceBreakdown[service] || 0) + 1;
      }
    });

    return { last6Months, avgOrderValue, topCustomers, serviceBreakdown };
  }, [orders]);

  // ── Filtering ──

  const filtered = orders.filter((o) => {
    const matchFilter = filter === "all" || o.status === filter;
    const matchSearch = !searchTerm ||
      o.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      o.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      o.userEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      o.trackingNumber?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchFilter && matchSearch;
  });

  const counts = {};
  counts.all = orders.length;
  Object.keys(ORDER_STATUSES).forEach((s) => {
    counts[s] = orders.filter((o) => o.status === s).length;
  });

  if (loading) {
    return (
      <div className="page-bg"><Navbar /><div className="container"><SkeletonList count={5} /></div></div>
    );
  }

  return (
    <div className="page-bg">
      <Navbar />
      <div className="container">
        <div className="animate-in">
          <h1 style={{ fontSize: 28, fontWeight: 800, marginBottom: 8 }}>Admin Dashboard</h1>
          <p style={{ color: "#64748b", fontSize: 14, marginBottom: 28 }}>
            Manage orders, send quotes, and upload labels.
          </p>

          {/* Stats row */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: 12, marginBottom: 28 }}>
            {[
              { key: "all", label: "Total", color: "#d4a053" },
              { key: "pending", label: "Awaiting Quote", color: "#f59e0b" },
              { key: "quoted", label: "Quoted", color: "#3b82f6" },
              { key: "confirmed", label: "Confirmed", color: "#8b5cf6" },
              { key: "completed", label: "Label Ready", color: "#10b981" },
              { key: "void_requested", label: "Void Req", color: "#f97316" },
              { key: "voided", label: "Voided", color: "#6b7280" },
            ].map((s) => (
              <div
                key={s.key}
                className={`card ${filter === s.key ? "stat-card-selected" : ""}`}
                style={{ padding: 16, textAlign: "center", cursor: "pointer" }}
                onClick={() => setFilter(s.key)}
              >
                <p style={{ fontSize: 28, fontWeight: 800, color: s.color, lineHeight: 1 }}>
                  {counts[s.key] || 0}
                </p>
                <p style={{ fontSize: 10, color: "#64748b", marginTop: 4, letterSpacing: 0.5, textTransform: "uppercase" }}>
                  {s.label}
                </p>
              </div>
            ))}
          </div>

          {/* Revenue Tracker */}
          <div className="card" style={{ padding: 20, marginBottom: 28 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
              <span style={{ fontSize: 16, fontWeight: 700, color: "#e2e8f0" }}>Revenue Tracker</span>
              <span style={{ fontSize: 11, color: "#64748b", background: "rgba(16,185,129,0.1)", padding: "2px 8px", borderRadius: 6, fontWeight: 600 }}>
                {revenue.orderCount} orders
              </span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 16 }}>
              <div style={{ padding: 14, borderRadius: 10, background: "rgba(16,185,129,0.06)", border: "1px solid rgba(16,185,129,0.15)" }}>
                <p style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Revenue</p>
                <p style={{ fontSize: 24, fontWeight: 800, color: "#10b981" }}>${revenue.totalRevenue.toFixed(2)}</p>
              </div>
              <div style={{ padding: 14, borderRadius: 10, background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.15)" }}>
                <p style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Label Costs</p>
                <p style={{ fontSize: 24, fontWeight: 800, color: "#ef4444" }}>${revenue.totalCost.toFixed(2)}</p>
              </div>
              <div style={{ padding: 14, borderRadius: 10, background: revenue.totalProfit >= 0 ? "rgba(16,185,129,0.06)" : "rgba(239,68,68,0.06)", border: `1px solid ${revenue.totalProfit >= 0 ? "rgba(16,185,129,0.15)" : "rgba(239,68,68,0.15)"}` }}>
                <p style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Profit</p>
                <p style={{ fontSize: 24, fontWeight: 800, color: revenue.totalProfit >= 0 ? "#10b981" : "#ef4444" }}>${revenue.totalProfit.toFixed(2)}</p>
              </div>
              <div style={{ padding: 14, borderRadius: 10, background: "rgba(212,160,83,0.06)", border: "1px solid rgba(212,160,83,0.15)" }}>
                <p style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Margin</p>
                <p style={{ fontSize: 24, fontWeight: 800, color: "#d4a053" }}>
                  {revenue.totalRevenue > 0 ? ((revenue.totalProfit / revenue.totalRevenue) * 100).toFixed(1) : "0.0"}%
                </p>
              </div>
            </div>
          </div>

          {/* Search + filter pills + CSV export + Analytics + Bulk actions */}
          <div style={{ marginBottom: 20 }}>
            <input
              className="sf-input"
              type="text"
              placeholder="Search orders, customers, tracking..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ marginBottom: 12, maxWidth: 400 }}
            />
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              {["all", "pending", "quoted", "confirmed", "completed", "void_requested", "voided"].map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  style={{
                    padding: "7px 18px", borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: "pointer",
                    border: "1px solid",
                    borderColor: filter === f ? "rgba(212,160,83,0.4)" : "rgba(255,255,255,0.06)",
                    background: filter === f ? "rgba(212,160,83,0.1)" : "transparent",
                    color: filter === f ? "#d4a053" : "#64748b",
                    transition: "all 0.2s", textTransform: "capitalize", fontFamily: "'DM Sans', sans-serif",
                  }}
                >
                  {f === "void_requested" ? "Void Req" : f} ({counts[f] || 0})
                </button>
              ))}

              <button
                className="btn btn-primary btn-sm"
                onClick={() => setShowAnalytics(!showAnalytics)}
                style={{ marginLeft: "auto", fontSize: 12, background: showAnalytics ? "rgba(212,160,83,0.2)" : undefined }}
              >
                Analytics
              </button>

              <button
                className={`btn btn-sm ${showActivityLog ? "btn-primary" : ""}`}
                onClick={() => setShowActivityLog(!showActivityLog)}
                style={{ fontSize: 12 }}
              >
                Activity Log
              </button>

              <button
                className="btn btn-primary btn-sm"
                onClick={() => { exportOrdersCSV(filtered); toast.success("CSV exported"); }}
                style={{ fontSize: 12 }}
              >
                Export CSV
              </button>
            </div>
          </div>

          {/* Analytics Panel */}
          {showAnalytics && (
            <div className="card" style={{ padding: 20, marginBottom: 28, background: "rgba(212,160,83,0.05)", border: "1px solid rgba(212,160,83,0.2)" }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, color: "#d4a053" }}>Analytics</h3>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 20 }}>
                {/* Revenue by Month */}
                <div>
                  <p style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", letterSpacing: 1, marginBottom: 12 }}>Revenue by Month (Last 6 Months)</p>
                  <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 120 }}>
                    {Object.entries(analytics.last6Months).map(([month, amount]) => {
                      const maxRevenue = Math.max(...Object.values(analytics.last6Months), 100);
                      const height = (amount / maxRevenue) * 100;
                      return (
                        <div key={month} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                          <div
                            style={{
                              width: "100%", height: `${height}%`, minHeight: 2, background: "linear-gradient(180deg, #d4a053, #9d7a47)", borderRadius: 4,
                            }}
                            title={`$${amount.toFixed(2)}`}
                          />
                          <span style={{ fontSize: 10, color: "#64748b", textAlign: "center" }}>{month.split(" ")[0]}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Key Metrics */}
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <div style={{ padding: 12, borderRadius: 8, background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.2)" }}>
                    <p style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: 1 }}>Avg Order Value</p>
                    <p style={{ fontSize: 20, fontWeight: 700, color: "#10b981" }}>${analytics.avgOrderValue.toFixed(2)}</p>
                  </div>
                </div>

                {/* Top Customers */}
                <div>
                  <p style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", letterSpacing: 1, marginBottom: 12 }}>Top 5 Customers</p>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {analytics.topCustomers.map(([customer, revenue]) => (
                      <div key={customer} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", borderRadius: 6, background: "rgba(255,255,255,0.03)" }}>
                        <span style={{ fontSize: 12, color: "#94a3b8" }}>{customer}</span>
                        <span style={{ fontSize: 12, fontWeight: 600, color: "#d4a053" }}>${revenue.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Service Breakdown */}
                <div>
                  <p style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", letterSpacing: 1, marginBottom: 12 }}>Orders by Service</p>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {Object.entries(analytics.serviceBreakdown).map(([service, count]) => {
                      const svc = SERVICES.find((s) => s.id === service);
                      return (
                        <div key={service} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", borderRadius: 6, background: "rgba(255,255,255,0.03)" }}>
                          <span style={{ fontSize: 12, color: "#94a3b8" }}>{svc?.name || service}</span>
                          <span style={{ fontSize: 12, fontWeight: 600, color: "#3b82f6" }}>{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Activity Log Panel */}
          {showActivityLog && (
            <div className="card" style={{ padding: 20, marginBottom: 28, background: "rgba(139,92,246,0.05)", border: "1px solid rgba(139,92,246,0.2)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: "#8b5cf6" }}>Activity Log</h3>
                <span style={{ fontSize: 11, color: "#64748b" }}>Last 50 actions</span>
              </div>
              <div style={{ maxHeight: 400, overflowY: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
                {activityLog.length === 0 ? (
                  <p style={{ color: "#64748b", fontSize: 13, textAlign: "center", padding: 20 }}>No activity yet</p>
                ) : (
                  activityLog.map((log) => (
                    <div key={log.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", borderRadius: 8, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.05)" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{
                          fontSize: 11, fontWeight: 700, padding: "3px 8px", borderRadius: 6, textTransform: "uppercase", letterSpacing: 0.5,
                          background: log.action.includes("void") || log.action.includes("Void") ? "rgba(239,68,68,0.12)" :
                            log.action.includes("Completed") || log.action.includes("Approved") ? "rgba(16,185,129,0.12)" :
                            log.action.includes("quote") || log.action.includes("Quote") ? "rgba(59,130,246,0.12)" :
                            log.action.includes("label") || log.action.includes("Label") ? "rgba(212,160,83,0.12)" :
                            "rgba(139,92,246,0.12)",
                          color: log.action.includes("void") || log.action.includes("Void") ? "#ef4444" :
                            log.action.includes("Completed") || log.action.includes("Approved") ? "#10b981" :
                            log.action.includes("quote") || log.action.includes("Quote") ? "#3b82f6" :
                            log.action.includes("label") || log.action.includes("Label") ? "#d4a053" :
                            "#8b5cf6",
                        }}>
                          {log.action}
                        </span>
                        {log.orderId && (
                          <span style={{ fontSize: 11, color: "#64748b", fontFamily: "monospace" }}>#{log.orderId.slice(0, 8)}</span>
                        )}
                        {log.details && (
                          <span style={{ fontSize: 12, color: "#94a3b8" }}>{log.details}</span>
                        )}
                      </div>
                      <span style={{ fontSize: 11, color: "#475569", whiteSpace: "nowrap" }}>
                        {log.timestamp ? formatFullDate(log.timestamp) : ""}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Bulk Actions Bar */}
          {selectedOrders.size > 0 && (
            <div className="card" style={{ padding: 16, marginBottom: 20, background: "rgba(212,160,83,0.08)", border: "1px solid rgba(212,160,83,0.25)" }}>
              <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: "#d4a053" }}>{selectedOrders.size} selected</span>

                {Array.from(selectedOrders).some((id) => {
                  const order = orders.find((o) => o.id === id);
                  return order && order.status === "pending";
                }) && (
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <input
                      className="sf-input"
                      type="number"
                      step="0.01"
                      placeholder="Price ($)"
                      value={bulkQuotePrice}
                      onChange={(e) => setBulkQuotePrice(e.target.value)}
                      style={{ width: 120, padding: "6px 10px" }}
                    />
                    <button className="btn btn-primary btn-sm" onClick={bulkQuoteOrders}>
                      Bulk Quote
                    </button>
                  </div>
                )}

                {Array.from(selectedOrders).some((id) => {
                  const order = orders.find((o) => o.id === id);
                  return order && order.status === "completed";
                }) && (
                  <button
                    className="btn btn-sm"
                    style={{ background: "rgba(239,68,68,0.12)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.25)" }}
                    onClick={bulkVoidOrders}
                  >
                    Bulk Void
                  </button>
                )}

                <button
                  className="btn btn-sm"
                  onClick={() => setSelectedOrders(new Set())}
                  style={{ marginLeft: "auto", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}
                >
                  Deselect All
                </button>
              </div>
            </div>
          )}

          {/* Orders */}
          {filtered.length === 0 ? (
            <div className="card" style={{ padding: 60, textAlign: "center" }}>
              <p style={{ color: "#64748b", fontSize: 16 }}>No orders matching this filter</p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {filtered.map((order) => {
                const svc = SERVICES.find((s) => s.id === order.service) || { name: order.service };
                const suggestedRaw = order.status === "pending" && order.service && order.weight
                  ? estimateShipping(order.service, order.weight, {
                    length: order.length || 0,
                    width: order.width || 0,
                    height: order.height || 0,
                  })
                  : null;
                const suggestedPrice = suggestedRaw ? parseFloat(suggestedRaw) : null;
                return (
                  <div key={order.id} className="card order-card" style={{ padding: 20 }}>
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 20, flexWrap: "wrap" }}>

                      {/* ── Checkbox for bulk actions ── */}
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <input
                          type="checkbox"
                          checked={selectedOrders.has(order.id)}
                          onChange={() => toggleSelectOrder(order.id)}
                          style={{ cursor: "pointer", width: 18, height: 18 }}
                        />
                      </div>

                      {/* ── Left: order info ── */}
                      <div style={{ flex: 1, minWidth: 280 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
                          <span style={{ color: "#64748b", fontSize: 12, fontFamily: "monospace" }}>
                            #{order.orderId || order.id.slice(0, 8)}
                          </span>
                          <StatusBadge status={order.status} />
                          <span style={{ fontSize: 12, color: "#475569" }}>
                            by <strong style={{ color: "#94a3b8" }}>{order.userName}</strong>
                          </span>
                          <span style={{ fontSize: 12, color: "#475569" }}>{order.userEmail}</span>
                          <span style={{ fontSize: 11, fontWeight: 700, color: (userBalances[order.userId] || 0) > 0 ? "#10b981" : "#ef4444",
                            background: (userBalances[order.userId] || 0) > 0 ? "rgba(16,185,129,0.1)" : "rgba(239,68,68,0.1)",
                            padding: "2px 8px", borderRadius: 6 }}>
                            Bal: ${(userBalances[order.userId] || 0).toFixed(2)}
                          </span>
                          <button className="btn btn-sm" style={{ fontSize: 10, padding: "2px 8px",
                            background: "rgba(212,160,83,0.1)", color: "#d4a053", border: "1px solid rgba(212,160,83,0.2)" }}
                            onClick={(e) => { e.stopPropagation(); setShowCreditModal({ uid: order.userId, name: order.userName, email: order.userEmail }); }}>
                            💰 Credit
                          </button>
                        </div>

                        {/* From → To */}
                        <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 16, alignItems: "start" }} className="grid-2">
                          <div>
                            <p style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>From</p>
                            <p style={{ color: "#e2e8f0", fontSize: 14, fontWeight: 600 }}>{order.senderName}</p>
                            <p style={{ color: "#94a3b8", fontSize: 13 }}>{order.senderStreet}</p>
                            <p style={{ color: "#94a3b8", fontSize: 13 }}>
                              {order.senderCity}, {order.senderState} {order.senderZip}
                            </p>
                          </div>
                          <div className="hide-mobile" style={{ color: "#d4a053", fontSize: 22, alignSelf: "center" }}>→</div>
                          <div>
                            <p style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>To</p>
                            <p style={{ color: "#e2e8f0", fontSize: 14, fontWeight: 600 }}>{order.recipientName}</p>
                            <p style={{ color: "#94a3b8", fontSize: 13 }}>{order.recipientStreet}</p>
                            <p style={{ color: "#94a3b8", fontSize: 13 }}>
                              {order.recipientCity}, {order.recipientState} {order.recipientZip}
                            </p>
                          </div>
                        </div>

                        {/* Details row */}
                        <div style={{ display: "flex", gap: 16, marginTop: 12, flexWrap: "wrap" }}>
                          <span style={{ fontSize: 12, color: "#64748b" }}>
                            Service: <strong style={{ color: "#94a3b8" }}>{svc.name}</strong>
                          </span>
                          {order.weight && (
                            <span style={{ fontSize: 12, color: "#64748b" }}>
                              Weight: <strong style={{ color: "#94a3b8" }}>{order.weight} lbs</strong>
                            </span>
                          )}
                          {order.length && (
                            <span style={{ fontSize: 12, color: "#64748b" }}>
                              Dims: <strong style={{ color: "#94a3b8" }}>{order.length}x{order.width}x{order.height} in</strong>
                            </span>
                          )}
                          {order.notes && (
                            <span style={{ fontSize: 12, color: "#64748b" }}>
                              Note: <em style={{ color: "#94a3b8" }}>{order.notes}</em>
                            </span>
                          )}
                          {(order.quotedPrice || order.price) && (
                            <span style={{ fontSize: 12, color: "#64748b" }}>
                              Quoted: <strong style={{ color: "#d4a053" }}>${order.quotedPrice || order.price?.toFixed(2)}</strong>
                            </span>
                          )}
                          {order.trackingNumber && (
                            <span style={{ fontSize: 12, color: "#64748b" }}>
                              Tracking:{" "}
                              <a
                                href={getUPSTrackingUrl(order.trackingNumber)}
                                target="_blank"
                                rel="noreferrer"
                                style={{ color: "#10b981", fontWeight: 600, textDecoration: "none" }}
                              >
                                {order.trackingNumber}
                              </a>
                              <button
                                onClick={() => { copyToClipboard(order.trackingNumber); toast.success("Copied!"); }}
                                style={{ marginLeft: 6, background: "none", border: "none", color: "#64748b", cursor: "pointer", fontSize: 11 }}
                              >
                                Copy
                              </button>
                            </span>
                          )}
                          {order.insurance && (
                            <span style={{ fontSize: 12, color: "#f59e0b" }}>Insurance</span>
                          )}
                          {order.signature && (
                            <span style={{ fontSize: 12, color: "#8b5cf6" }}>Signature Req.</span>
                          )}
                        </div>
                      </div>

                      {/* ── Right: admin actions ── */}
                      <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-end", minWidth: 220 }}>

                        {/* PENDING → Send Quote */}
                        {order.status === "pending" && (
                          <div style={{ display: "flex", flexDirection: "column", gap: 6, width: "100%" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "space-between" }}>
                              <label style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: 1 }}>
                                Send Quote ($)
                              </label>
                              {suggestedPrice && (
                                <span style={{ fontSize: 11, color: "#10b981", fontStyle: "italic" }}>
                                  Suggested: ${suggestedPrice.toFixed(2)}
                                </span>
                              )}
                            </div>
                            <div style={{ display: "flex", gap: 8 }}>
                              <input
                                className="sf-input"
                                type="number"
                                step="0.01"
                                placeholder={suggestedPrice ? suggestedPrice.toFixed(2) : "8.50"}
                                value={quoteInputs[order.id] || ""}
                                onChange={(e) => setQuoteInputs((p) => ({ ...p, [order.id]: e.target.value }))}
                                style={{ flex: 1 }}
                              />
                              <button className="btn btn-primary btn-sm" onClick={() => sendQuote(order.id)}>
                                Send
                              </button>
                            </div>
                          </div>
                        )}

                        {/* QUOTED → Waiting / Edit Quote */}
                        {order.status === "quoted" && (
                          <div style={{ display: "flex", flexDirection: "column", gap: 6, width: "100%" }}>
                            {editingQuote === order.id ? (
                              <>
                                <label style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: 1 }}>
                                  Update Quote ($)
                                </label>
                                <div style={{ display: "flex", gap: 8 }}>
                                  <input
                                    className="sf-input"
                                    type="number"
                                    step="0.01"
                                    placeholder={order.quotedPrice || order.price?.toFixed(2)}
                                    defaultValue={order.quotedPrice || order.price?.toFixed(2)}
                                    id={`edit-quote-${order.id}`}
                                    style={{ flex: 1 }}
                                  />
                                  <button
                                    className="btn btn-primary btn-sm"
                                    onClick={() => {
                                      const input = document.getElementById(`edit-quote-${order.id}`);
                                      saveQuoteEdit(order.id, input.value);
                                    }}
                                  >
                                    Save
                                  </button>
                                  <button
                                    className="btn btn-sm"
                                    style={{ background: "rgba(239,68,68,0.12)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.25)" }}
                                    onClick={() => setEditingQuote(null)}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </>
                            ) : (
                              <>
                                <p style={{ fontSize: 13, color: "#3b82f6", fontStyle: "italic" }}>
                                  Waiting for customer to confirm ${order.quotedPrice || order.price?.toFixed(2)}
                                </p>
                                <button
                                  className="btn btn-sm"
                                  style={{ background: "rgba(59,130,246,0.12)", color: "#3b82f6", border: "1px solid rgba(59,130,246,0.25)" }}
                                  onClick={() => setEditingQuote(order.id)}
                                >
                                  Edit Quote
                                </button>
                              </>
                            )}
                          </div>
                        )}

                        {/* CONFIRMED → Upload Label + Tracking + Complete */}
                        {order.status === "confirmed" && (
                          <div style={{ display: "flex", flexDirection: "column", gap: 6, width: "100%" }}>
                            {order.labelData && (
                              <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: 6, background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.2)" }}>
                                <span style={{ fontSize: 12, color: "#10b981", fontWeight: 600 }}>Label uploaded</span>
                                <span style={{ fontSize: 11, color: "#64748b" }}>({order.labelFileName})</span>
                              </div>
                            )}
                            <label style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: 1 }}>
                              Tracking #
                            </label>
                            <div style={{ display: "flex", gap: 8 }}>
                              <input
                                className="sf-input"
                                placeholder="1Z999AA10123456784"
                                value={trackingInputs[order.id] || ""}
                                onChange={(e) => setTrackingInputs((p) => ({ ...p, [order.id]: e.target.value }))}
                                style={{ flex: 1 }}
                              />
                            </div>
                            <input
                              type="file"
                              accept=".pdf,.png,.jpg,.jpeg"
                              ref={(el) => (fileRefs.current[order.id] = el)}
                              style={{ display: "none" }}
                              onChange={(e) => uploadLabel(order.id, e.target.files[0])}
                            />
                            <div style={{ display: "flex", gap: 8 }}>
                              <button
                                className="btn btn-primary btn-sm"
                                style={{ flex: 1, justifyContent: "center" }}
                                onClick={() => fileRefs.current[order.id]?.click()}
                                disabled={uploading[order.id]}
                              >
                                {uploading[order.id] ? "Uploading..." : (order.labelData ? "Replace Label" : "Upload Label")}
                              </button>
                              <button
                                className="btn btn-sm"
                                style={{ flex: 1, justifyContent: "center", background: "rgba(16,185,129,0.12)", color: "#10b981", border: "1px solid rgba(16,185,129,0.25)" }}
                                onClick={() => completeWithTracking(order.id)}
                                disabled={!trackingInputs[order.id]?.trim()}
                              >
                                Complete
                              </button>
                            </div>
                          </div>
                        )}

                        {/* VOID_REQUESTED → Approve / Deny */}
                        {order.status === "void_requested" && (
                          <div style={{ display: "flex", flexDirection: "column", gap: 6, width: "100%" }}>
                            <label style={{ fontSize: 11, color: "#f97316", textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>
                              Void Request
                            </label>
                            <div style={{ display: "flex", gap: 8 }}>
                              <button
                                className="btn btn-sm"
                                style={{ flex: 1, background: "rgba(16,185,129,0.12)", color: "#10b981", border: "1px solid rgba(16,185,129,0.25)" }}
                                onClick={() => handleVoid(order.id, true)}
                              >
                                Approve
                              </button>
                              <button
                                className="btn btn-sm"
                                style={{ flex: 1, background: "rgba(239,68,68,0.12)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.25)" }}
                                onClick={() => handleVoid(order.id, false)}
                              >
                                Deny
                              </button>
                            </div>
                          </div>
                        )}

                        {/* COMPLETED → View Label + Cost + Timeline + Void */}
                        {order.status === "completed" && (
                          <div style={{ display: "flex", flexDirection: "column", gap: 6, width: "100%" }}>
                            {(order.labelData || order.labelUrl) && (
                              <button
                                className="btn btn-sm"
                                style={{ background: "rgba(16,185,129,0.12)", color: "#10b981", border: "1px solid rgba(16,185,129,0.25)", justifyContent: "center" }}
                                onClick={() => {
                                  const data = order.labelData || order.labelUrl;
                                  const link = document.createElement("a");
                                  link.href = data;
                                  link.download = order.labelFileName || "label.pdf";
                                  link.click();
                                }}
                              >
                                View Label
                              </button>
                            )}
                            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                              <label style={{ fontSize: 11, color: "#64748b", whiteSpace: "nowrap" }}>Cost $</label>
                              {order.adminCost ? (
                                <span style={{ fontSize: 13, fontWeight: 600, color: "#ef4444" }}>${parseFloat(order.adminCost).toFixed(2)}</span>
                              ) : (
                                <>
                                  <input
                                    className="sf-input"
                                    type="number"
                                    step="0.01"
                                    placeholder="0.00"
                                    value={costInputs[order.id] || ""}
                                    onChange={(e) => setCostInputs((p) => ({ ...p, [order.id]: e.target.value }))}
                                    style={{ flex: 1, padding: "4px 8px", fontSize: 12 }}
                                  />
                                  <button className="btn btn-sm" style={{ padding: "4px 10px", fontSize: 11 }} onClick={() => saveCost(order.id)}>Save</button>
                                </>
                              )}
                            </div>
                            <div style={{ display: "flex", gap: 8 }}>
                              <button
                                className="btn btn-sm"
                                style={{ flex: 1, background: "rgba(138,117,255,0.12)", color: "#8b75ff", border: "1px solid rgba(138,117,255,0.25)" }}
                                onClick={() => toggleTimeline(order.id)}
                              >
                                Timeline
                              </button>
                              <button
                                className="btn btn-sm"
                                style={{ flex: 1, background: "rgba(239,68,68,0.12)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.25)" }}
                                onClick={() => adminVoidOrder(order.id)}
                              >
                                Void Order
                              </button>
                            </div>
                          </div>
                        )}

                        {/* VOIDED indicator */}
                        {order.status === "voided" && (
                          <p style={{ fontSize: 13, color: "#6b7280", fontStyle: "italic" }}>Label voided</p>
                        )}
                      </div>
                    </div>

                    {/* Timeline */}
                    {expandedTimeline.has(order.id) && (
                      <div style={{ marginTop: 20, paddingTop: 20, borderTop: "1px solid rgba(255,255,255,0.05)" }}>
                        <p style={{ fontSize: 12, fontWeight: 600, color: "#d4a053", marginBottom: 12 }}>Order Timeline</p>
                        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                          {[
                            { label: "Created", timestamp: order.createdAt },
                            { label: "Quoted", timestamp: order.quotedAt },
                            { label: "Confirmed", timestamp: order.confirmedAt },
                            { label: "Label Uploaded", timestamp: order.labelUploadedAt },
                            { label: "Completed", timestamp: order.completedAt },
                            { label: "Void Requested", timestamp: order.voidRequestedAt },
                            { label: "Void Resolved", timestamp: order.voidResolvedAt },
                          ].map((event, idx) => {
                            if (!event.timestamp) return null;
                            const date = event.timestamp.toDate ? event.timestamp.toDate() : new Date(event.timestamp);
                            return (
                              <div key={idx} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 24 }}>
                                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#d4a053", marginBottom: 8 }} />
                                  {idx < 6 && <div style={{ width: 2, height: 20, background: "rgba(212,160,83,0.3)" }} />}
                                </div>
                                <div style={{ paddingTop: 2 }}>
                                  <p style={{ fontSize: 12, fontWeight: 600, color: "#e2e8f0" }}>{event.label}</p>
                                  <p style={{ fontSize: 11, color: "#64748b" }}>{formatFullDate(date)}</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Credit Modal ── */}
      {showCreditModal && (
        <div style={{ position: "fixed", inset: 0, zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center",
          background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }}
          onClick={() => setShowCreditModal(null)}>
          <div className="card animate-slide-up" style={{ padding: 28, width: 400, maxWidth: "90vw" }}
            onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: 18, fontWeight: 800, marginBottom: 4 }}>💰 Manage Balance</h3>
            <p style={{ fontSize: 13, color: "#94a3b8", marginBottom: 16 }}>
              {showCreditModal.name || showCreditModal.email}
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16,
              padding: 14, borderRadius: 10, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
              <span style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: 1 }}>Current Balance</span>
              <span style={{ fontSize: 24, fontWeight: 800, marginLeft: "auto",
                color: (userBalances[showCreditModal.uid] || 0) > 0 ? "#10b981" : "#ef4444" }}>
                ${(userBalances[showCreditModal.uid] || 0).toFixed(2)}
              </span>
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              <button className={`btn btn-sm ${creditType === "add" ? "" : "btn-ghost"}`}
                style={creditType === "add" ? { flex: 1, background: "rgba(16,185,129,0.15)", color: "#10b981", border: "1px solid rgba(16,185,129,0.3)" }
                  : { flex: 1 }}
                onClick={() => setCreditType("add")}>
                + Add Funds
              </button>
              <button className={`btn btn-sm ${creditType === "deduct" ? "" : "btn-ghost"}`}
                style={creditType === "deduct" ? { flex: 1, background: "rgba(239,68,68,0.15)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.3)" }
                  : { flex: 1 }}
                onClick={() => setCreditType("deduct")}>
                − Deduct
              </button>
            </div>
            <input className="sf-input" type="number" step="0.01" min="0" placeholder="Amount ($)"
              value={creditAmount} onChange={e => setCreditAmount(e.target.value)}
              style={{ marginBottom: 10, width: "100%" }} />
            <input className="sf-input" placeholder="Note (e.g. Zelle payment received)"
              value={creditNote} onChange={e => setCreditNote(e.target.value)}
              style={{ marginBottom: 16, width: "100%" }} />
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={handleCreditUser}>
                {creditType === "add" ? "Add Credit" : "Deduct Credit"}
              </button>
              <button className="btn btn-ghost" onClick={() => { setShowCreditModal(null); setCreditAmount(""); setCreditNote(""); }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Chat */}
      <ChatBubble orders={orders} />
    </div>
  );
}
